import { jwtDecode } from 'jwt-decode';

export interface AuthTokens {
  id?: string;
  accessToken: string;
  refreshToken: string;
  accessTokenExpiredAt?: string;
  refreshTokenExpiredAt?: string;
}

interface DecodedToken {
  'X-SC-Role': string;
  'X-SC-ID': string;
  'X-SC-Session-ID': string;
  'X-SC-Nonce': string;
  'X-SC-TokenType': string;
  exp: number;
  iss: string;
  aud: string;
}

/** JWT is header.payload.signature — required before jwtDecode (avoids InvalidTokenError). */
export const isValidJWT = (token: string | null | undefined): token is string =>
  typeof token === 'string' && token.trim().split('.').length === 3;

export const normalizeToken = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  let t = raw.trim();
  if (t.startsWith('Bearer ')) t = t.slice(7).trim();
  return isValidJWT(t) ? t : null;
};

export const getAccessToken = (): string | null => normalizeToken(localStorage.getItem('accessToken'));

/** Backend may return tokens at root or under `data` (envelope). */
export const extractAuthPayload = (data: unknown): AuthTokens | null => {
  const p = ((data as { data?: AuthTokens })?.data ?? data) as AuthTokens;
  return normalizeToken(p?.accessToken) && normalizeToken(p?.refreshToken) ? p : null;
};

export const persistAuthTokens = (p: AuthTokens, username?: string): boolean => {
  const access = normalizeToken(p.accessToken);
  const refresh = normalizeToken(p.refreshToken);
  if (!access || !refresh) return false;
  localStorage.setItem('accessToken', access);
  localStorage.setItem('refreshToken', refresh);
  if (p.id) localStorage.setItem('userId', p.id);
  if (username) localStorage.setItem('username', username);
  if (p.accessTokenExpiredAt) localStorage.setItem('accessTokenExpiredAt', p.accessTokenExpiredAt);
  if (p.refreshTokenExpiredAt) localStorage.setItem('refreshTokenExpiredAt', p.refreshTokenExpiredAt);
  return true;
};

export const isTokenExpired = (token: string): boolean => {
  if (!isValidJWT(token)) return true;
  try {
    return JSON.parse(atob(token.split('.')[1])).exp < Date.now() / 1000;
  } catch {
    return true;
  }
};

export const isTokenExpiringSoon = (token: string, minutesBeforeExpiry = 5): boolean => {
  if (!isValidJWT(token)) return true;
  try {
    return (JSON.parse(atob(token.split('.')[1])).exp - Date.now() / 1000) / 60 <= minutesBeforeExpiry;
  } catch {
    return true;
  }
};

export const decodeAccessToken = (): DecodedToken | null => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
};

export const getCurrentUserRole = (): string | null => decodeAccessToken()?.['X-SC-Role'] ?? null;

export const getCurrentUserId = (): string | null => decodeAccessToken()?.['X-SC-ID'] ?? null;

export const getCurrentUserInfo = () => ({
  role: getCurrentUserRole(),
  id: getCurrentUserId(),
});

export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  return token ? !isTokenExpired(token) : false;
};

export const shouldRefreshToken = (): boolean => {
  const token = getAccessToken();
  return token ? isTokenExpiringSoon(token, 5) : false;
};

export const clearAuthData = (): void => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('username');
  localStorage.removeItem('userId');
  localStorage.removeItem('accessTokenExpiredAt');
  localStorage.removeItem('refreshTokenExpiredAt');
};

export const logout = (): void => {
  clearAuthData();
  window.location.href = '/login';
};
